import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { ActivityConfirmation } from 'src/app/_model/activity-confirmation';
import { Servicejob } from 'src/app/_model/servicejob';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

import { ActivityConfirmationsService, CloudDraftInfo } from '../activity-confirmations.service';
import { ActivityConfirmationsDaoService } from '../../dao/activity-confirmations-dao.service';
import { EmployeeService } from 'src/app/_services/employee/logic/employee.service';
import { KeyValuePersistenceService } from 'src/app/_services/persistence/key-value-persistence/key-value-persistence.service';
import { AcStatus } from 'src/app/_model/dyn-form-data';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { DynFormsService } from 'src/app/_services/dyn-forms/logic/dyn-forms.service';
import { BadgesService } from 'src/app/_services/badges/logic/badges.service';

/**
 * Loads the ims/ActivityConfirmations relevant for this user
 */
@Injectable()
export class ActivityConfirmationsLogicService implements ActivityConfirmationsService {
  public activityConfirmations$ = new BehaviorSubject<ActivityConfirmation[]>([]);
  public cloudDraftInfos$ = new BehaviorSubject<CloudDraftInfo[]>([]);
  public lastSyncTstamp$ = new BehaviorSubject<number>(undefined);

  /** Lazily resolved - DynFormsService pulls in a large dependency graph */
  private get dynFormsService(): DynFormsService {
    return this.injector.get(DynFormsService);
  }
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  private static readonly CONSUMED_DRAFT_AC_IDS_KEY = 'consumedDraftAcIds';
  private static readonly CLOUD_DRAFT_INFOS_KEY = 'cloudDraftInfos';
  private static readonly LAST_SYNC_TSTAMP_KEY = 'activityConfirmationsLastSync';
  /** Guards the history walk against a corrupt chain linking back on itself */
  private static readonly MAX_HISTORY_VERSIONS = 100;

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private activityConfirmationsDaoService: ActivityConfirmationsDaoService,
    private authenticationService: AuthenticationService,
    private servicejobsService: ServicejobsService,
    private employeeService: EmployeeService,
    private keyValueService: KeyValuePersistenceService,
    private appService: AppService,
    private badgesService: BadgesService,
    private injector: Injector,
  ) {
    this.initialize();
  }

  /**
   * The head of a document's AC chain.
   *
   * KRK-45: matched on the chain root, which is the only stable identity - a smart
   * renumbering (LBW-251) rewrites the acukey of every version it touches, so an
   * acukey lookup silently returned nothing (or, worse, the leftovers of the old
   * numbering) once a colleague filed an earlier report. The acukey is used only when
   * no chain root is known, i.e. for rows written before KRK-45.
   */
  public getActivityConfirmationByChain(
    chainRootAcId: number | undefined,
    acukey?: string,
  ): ActivityConfirmation {
    if (chainRootAcId == undefined && acukey == undefined) {
      return undefined;
    }

    /**
     * Note it compares the chain root as a VALUE and never resolves it: the root AC
     * itself does not have to be present. The sync delivers at most 50 previous versions
     * per document (MAX_AC_HISTORY_DEPTH in the backend), so on a long chain the root row
     * is simply missing - every delivered version still carries its id.
     *
     * An AC without a chain root at all (a row no backfill has reached, or one created
     * outside the mobile upload paths) would match nothing here, because chainRoot()
     * answers with its own id. Those are matched by acukey in the second pass below.
     */
    const byChainRoot = (activityConfirmation: ActivityConfirmation): boolean =>
      activityConfirmation.chainRootAcId != undefined &&
      activityConfirmation.chainRootAcId === chainRootAcId;
    const byAcukey = (activityConfirmation: ActivityConfirmation): boolean =>
      acukey != undefined && activityConfirmation.acukey === acukey;

    // More than one version of the same chain is present - since APILevel 15 the sync
    // even delivers the history. Answer with the current version; only if the chain has
    // none (conflict versions only) fall back to the highest id, so callers get
    // something rather than nothing.
    const pick = (
      predicate: (activityConfirmation: ActivityConfirmation) => boolean,
    ): ActivityConfirmation => {
      const versions = this.activityConfirmations$
        .getValue()
        .filter(predicate)
        .sort((a, b) => (b.getImsId() ?? 0) - (a.getImsId() ?? 0));
      return (
        versions.find((activityConfirmation) => activityConfirmation.isCurrentVersion()) ??
        versions.shift()
      );
    };

    return (
      (chainRootAcId != undefined ? pick(byChainRoot) : undefined) ?? pick(byAcukey)
    );
  }

  public getActivityConfirmationByAcukey(acukey: string): ActivityConfirmation {
    return this.getActivityConfirmationByChain(undefined, acukey);
  }

  public getActivityConfirmationById(acId: number): ActivityConfirmation {
    if (acId == undefined) {
      return undefined;
    }
    return this.activityConfirmations$
      .getValue()
      .find((activityConfirmation) => activityConfirmation.getImsId() === acId);
  }

  public getVersionHistory(acId: number): ActivityConfirmation[] {
    const history: ActivityConfirmation[] = [];
    const visited = new Set<number>();
    let current = this.getActivityConfirmationById(acId);

    while (
      current != undefined &&
      !visited.has(current.getImsId()) &&
      history.length < ActivityConfirmationsLogicService.MAX_HISTORY_VERSIONS
    ) {
      visited.add(current.getImsId());
      history.push(current);
      if (current.previousVersionActConfId == undefined) {
        break;
      }
      const previous = this.getActivityConfirmationById(current.previousVersionActConfId);
      if (previous == undefined) {
        // The sync caps the delivered history (50 versions per document), so hitting a
        // link whose target is not local is normal and simply ends the list.
        this.logger.log(
          this.constructor.name,
          'getVersionHistory - predecessor not synced, history ends at',
          current.getImsId(),
        );
        break;
      }
      current = previous;
    }

    return history;
  }

  public async updateFilledFormData(acId: number, filledFormData: string): Promise<void> {
    if (acId == undefined) {
      this.logger.error(
        this.constructor.name,
        'Cannot update filledFormData without an acId',
      );
      return;
    }
    await this.activityConfirmationsDaoService.updateFilledFormData(acId, filledFormData);
    await this.loadLocalActivityConfirmations();
    await this.updateCloudDraftInfos();
  }

  /**
   * Stores an AC that arrived outside of a sync - currently the `acdto` of a
   * dynform upload response. Same authoritative record the sync would deliver,
   * so it goes into the same table; the next sync simply replaces it.
   */
  public async saveActivityConfirmation(entry: ActivityConfirmation): Promise<void> {
    await this.activityConfirmationsDaoService.saveActivityConfirmations([entry]);
    await this.loadLocalActivityConfirmations();
    await this.updateCloudDraftInfos();
  }

  // Resetable
  public async reset() {
    await this.activityConfirmationsDaoService.reset();
    // The reset drops the local form data, i.e. exactly the rows a consumed marker
    // stands in for. Keeping the markers left the device with neither: no local draft
    // and the cloud draft suppressed - "Zurücksetzen + neu syncen" made a missing
    // document permanently invisible instead of bringing it back.
    await this.keyValueService.delete(
      ActivityConfirmationsLogicService.CONSUMED_DRAFT_AC_IDS_KEY,
    );
    await this.loadLocalActivityConfirmations();
    // No ACs left, so this produces an empty list regardless of me$
    await this.updateCloudDraftInfos();
  }

  public async clear() {
    await this.activityConfirmationsDaoService.clear();
    // Hard reset semantics: the 'already taken over' bookkeeping goes too, so
    // drafts are offered again from scratch.
    await this.keyValueService.delete(
      ActivityConfirmationsLogicService.CONSUMED_DRAFT_AC_IDS_KEY,
    );
    // No data left, so there is nothing a "last synced" timestamp could describe
    await this.keyValueService.delete(
      ActivityConfirmationsLogicService.LAST_SYNC_TSTAMP_KEY,
    );
    this.lastSyncTstamp$.next(undefined);
    await this.loadLocalActivityConfirmations();
    // No ACs left, so this produces an empty list regardless of me$
    await this.updateCloudDraftInfos();
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    // Deliberately no clear() here - the local ACs are only replaced once the
    // complete remote set has actually arrived, see loadRemoteActivityConfirmations.
    await this.loadRemoteActivityConfirmations(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}

  public async postsync(): Promise<void> {
    await this.cleanupStaleDraftChains();
  }

  /**
   * APP4-948: drops the passive local copy of every chain that no longer belongs to
   * this device - see staleLocalMirrorReason() for the three cases. Locally edited
   * (dirty) rows are kept: their queued upload turns into a conflict version, which
   * is what keeps offline work from being thrown away.
   *
   * Used to live in ExtFilesLogicService.restoreDraftFormData(), which was driven by
   * the ExtFiles and therefore blind to chains whose ExtFile is never synced.
   */
  private async cleanupStaleDraftChains(): Promise<void> {
    const myEmpId = this.employeeService.me$.getValue()?.id?.imsId;

    for (const activityConfirmation of this.activityConfirmations$.getValue()) {
      if (
        // A superseded version says nothing about where the chain stands today -
        // only its head does. Without this every historical version would trigger
        // a cleanup for the whole chain.
        !activityConfirmation.isCurrentVersion()
      ) {
        continue;
      }

      const reason = this.staleLocalMirrorReason(activityConfirmation, myEmpId);
      if (reason == undefined) {
        continue;
      }

      try {
        this.logger.log(
          this.constructor.name,
          'cleanupStaleDraftChains - dropping passive local copies of chain root',
          activityConfirmation.chainRoot(),
          'acukey',
          activityConfirmation.acukey,
          '-',
          reason,
        );
        await this.dynFormsService.deleteNotDirtyDraftsForChain(
          activityConfirmation.chainRoot(),
          activityConfirmation.acukey,
        );
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'cleanupStaleDraftChains - failed for chain root',
          activityConfirmation.chainRoot(),
          error?.message,
        );
      }
    }
  }

  /**
   * Why a passive local copy of this chain must go, or undefined when it may stay.
   *
   * - finished: the final submit clears the draft ownership. A safety net only, NOT
   *   the primary mechanism - such an AC drops out of the draft branch of the sync
   *   and never shows up here for a form with draftextfiles=false. The
   *   `draft_finalized` push is the reliable trigger, see DraftFinalizedActionHandler.
   * - parked on a service job: the document went into the Postkorb and belongs to
   *   whoever picks it up. Nothing was pushed at the previous owner's other devices
   *   to tell them, so without this the row sat in "Meine Formulare" forever while
   *   the same document was already waiting in the Postkorb.
   * - handed to a colleague: same thing with a person instead of a job.
   *
   * A draft still owned by ME is explicitly NOT stale, no matter which device holds
   * the baton: that local row is the anchor the claim-on-open works on, and dropping
   * it would turn every handover between my own devices into a new entry.
   */
  private staleLocalMirrorReason(
    activityConfirmation: ActivityConfirmation,
    myEmpId: number | undefined,
  ): string | undefined {
    if (activityConfirmation.draftOwnerServiceJobId != null) {
      return `parked on service job ${activityConfirmation.draftOwnerServiceJobId}`;
    }
    if (
      activityConfirmation.draftOwnerEmpId != null &&
      myEmpId != undefined &&
      activityConfirmation.draftOwnerEmpId != myEmpId
    ) {
      return `owned by employee ${activityConfirmation.draftOwnerEmpId}, not me (${myEmpId})`;
    }
    if (
      activityConfirmation.draftOwnerEmpId == null &&
      activityConfirmation.draftOwnerServiceJobId == null &&
      activityConfirmation.acStatus != null &&
      activityConfirmation.acStatus >= AcStatus.READY_APPROVED
    ) {
      return `chain finished (acStatus ${activityConfirmation.acStatus})`;
    }
    return undefined;
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalActivityConfirmations();
      // Restore the draft list from the last run instead of recomputing it:
      // employeeService.me$ is still empty here, so the draft owner check would
      // find nothing. Recomputing happens on the next sync / AC change.
      await this.loadPersistedCloudDraftInfos();
      const lastSync: number | undefined = await this.keyValueService.load(
        ActivityConfirmationsLogicService.LAST_SYNC_TSTAMP_KEY,
      );
      this.lastSyncTstamp$.next(lastSync);
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {}

  public async loadConsumedDraftAcIds(): Promise<Set<number>> {
    const stored: number[] | undefined = await this.keyValueService.load(
      ActivityConfirmationsLogicService.CONSUMED_DRAFT_AC_IDS_KEY,
    );
    return new Set<number>(stored ?? []);
  }

  /**
   * KRK-45: a takeover, expressed the way the rest of the code already reads ownership -
   * this employee, this device - instead of a separate "consumed" marker.
   *
   * Why not markDraftConsumed(): that marker is permanent and local. The takeover hides
   * the draft with it immediately, but the ownership only really moves at the backend
   * with the first upload. If that never happens - the user only looked at the document,
   * or went offline, or reset the app - the draft stayed hidden on this device forever
   * while the backend kept offering it everywhere else. Worse for a draft out of the
   * Postkorb: the AC still said `parked on service job`, so isStaleLocalMirror() hid the
   * local row too and cleanupStaleDraftChains() deleted it on the next sync - the
   * document was gone from BOTH groups on the device that had just taken it over.
   *
   * Writing the ownership instead makes every consumer agree without a second mechanism:
   * the draft is not offered for takeover anymore (the baton is here), the local row is
   * not a stale mirror, and nothing deletes it. And it is self-healing - the AC table is
   * replaced wholesale on every sync, so the backend has the last word: if the takeover
   * did stick, the synced AC says the same; if it did not, the document goes back into
   * the Postkorb on this device too instead of staying invisible.
   */
  public async claimDraftLocally(acId: number): Promise<void> {
    if (acId == undefined) {
      return;
    }

    const myEmpId = this.employeeService.me$.getValue()?.id?.imsId;
    const tokenId = await this.appService.getTokenId();
    const myMcId = tokenId != null ? +tokenId : undefined;

    if (myEmpId == undefined || myMcId == undefined) {
      // Without both halves of the identity the ownership cannot be written in a way
      // updateCloudDraftInfos() would recognise, and the draft would be offered again
      // right next to the row the takeover just created. Fall back to the old marker.
      this.logger.warn(
        this.constructor.name,
        'claimDraftLocally - no employee/device identity yet, falling back to consumed for',
        acId,
      );
      await this.markDraftConsumed(acId);
      return;
    }

    await this.activityConfirmationsDaoService.updateDraftOwner(
      acId,
      Number(myEmpId),
      myMcId,
    );
    await this.loadLocalActivityConfirmations();
    this.logger.log(
      this.constructor.name,
      'claimDraftLocally - draft',
      acId,
      'is owned by me on this device until a sync says otherwise',
    );
    await this.updateCloudDraftInfos();
  }

  public async markDraftConsumed(acId: number): Promise<void> {
    if (acId == undefined) {
      return;
    }
    const consumed = await this.loadConsumedDraftAcIds();
    consumed.add(acId);
    await this.keyValueService.store(
      ActivityConfirmationsLogicService.CONSUMED_DRAFT_AC_IDS_KEY,
      Array.from(consumed),
    );
    await this.updateCloudDraftInfos();
  }

  /**
   * Rebuilds the list of drafts offered for takeover. Driven by the ACs, so a draft
   * whose ExtFile is never synced shows up too.
   */
  public async unmarkDraftConsumed(acId: number): Promise<void> {
    if (acId == undefined) {
      return;
    }
    const consumed = await this.loadConsumedDraftAcIds();
    if (!consumed.delete(acId)) {
      return;
    }
    await this.keyValueService.store(
      ActivityConfirmationsLogicService.CONSUMED_DRAFT_AC_IDS_KEY,
      Array.from(consumed),
    );
    this.logger.log(
      this.constructor.name,
      'unmarkDraftConsumed - draft',
      acId,
      'is offered again',
    );
    await this.updateCloudDraftInfos();
  }

  public async updateCloudDraftInfos(): Promise<void> {
    const me = this.employeeService.me$.getValue();
    const tokenId = await this.appService.getTokenId();
    const myMcId = tokenId != null ? +tokenId : undefined;
    const consumed = await this.loadConsumedDraftAcIds();
    const infos: CloudDraftInfo[] = [];
    const rejected: string[] = [];

    for (const activityConfirmation of this.activityConfirmations$.getValue()) {
      const acId = activityConfirmation.getImsId();
      // Only ACs that actually carry a draft payload are candidates - everything
      // else is not a draft and must not be logged as a rejection either.
      const isDraftCandidate = activityConfirmation.filledFormData != null;

      if (acId == undefined || consumed.has(acId)) {
        if (isDraftCandidate) {
          rejected.push(`${acId}: already consumed`);
        }
        continue;
      }

      // Since APILevel 15 the sync delivers the complete version chain. All versions
      // of my draft carry the same draftOwnerEmpId/McId, so without this every one of
      // them would be offered as a separate draft with the same acukey. Historical
      // versions have no filledFormData, so they are not even draft candidates -
      // hence no rejection log, and hence this cannot replace the payload check.
      if (!activityConfirmation.isCurrentVersion()) {
        if (isDraftCandidate) {
          rejected.push(
            `${acId}: not the current version (next=${activityConfirmation.nextVersionActConfId}` +
              ` conflict=${activityConfirmation.isConflictVersion})`,
          );
        }
        continue;
      }

      // Owned by me and NOT currently held by this device - a draft the baton of
      // which sits here is already present as a local form.
      // Note: NOT `draftOwnerMcId == null`. With the APP4-948 cloud autosave the
      // owning device is always set, so that older condition filtered out exactly
      // the drafts this feature is about.
      const isEmpDraft =
        activityConfirmation.draftOwnerEmpId == me?.id?.imsId &&
        (activityConfirmation.draftOwnerMcId == null ||
          activityConfirmation.draftOwnerMcId !== myMcId);
      const isServiceJobDraft = activityConfirmation.draftOwnerServiceJobId != null;

      if (!isEmpDraft && !isServiceJobDraft) {
        if (isDraftCandidate) {
          rejected.push(
            `${acId}: no draft owner for me (empId=${activityConfirmation.draftOwnerEmpId}` +
              ` me=${me?.id?.imsId} mcId=${activityConfirmation.draftOwnerMcId} myMcId=${myMcId}` +
              ` sjobId=${activityConfirmation.draftOwnerServiceJobId})`,
          );
        }
        continue;
      }

      // A locked AC is not offered as a draft anymore. `acIsLocked` is the single
      // source of truth for that (APILevel 15): the backend already folds a
      // finishing status and foreign ownership into it, so the app must not rebuild
      // the same decision from acStatus/draftOwnerEmpId and risk disagreeing with it.
      if (activityConfirmation.acIsLocked) {
        if (isDraftCandidate) {
          rejected.push(`${acId}: locked (acStatus ${activityConfirmation.acStatus})`);
        }
        continue;
      }

      infos.push({
        acId,
        chainRootAcId: activityConfirmation.chainRoot(),
        acukey: activityConfirmation.acukey,
        label: activityConfirmation.label,
        numberRangeValue: activityConfirmation.numberRangeValue,
        refTstamp: activityConfirmation.refTstamp,
        draftOwnerEmpId: activityConfirmation.draftOwnerEmpId,
        eventTstamp: activityConfirmation.eventTstamp,
        formId: activityConfirmation.dynFormId,
        objRef: activityConfirmation.ownerObjRef,
        ownerId: activityConfirmation.ownerId,
        reportFileId: activityConfirmation.reportFileId,
        draftOwnerServiceJobId: activityConfirmation.draftOwnerServiceJobId,
        acStatus: activityConfirmation.acStatus,
        savedTstamp: activityConfirmation.entityCreatedTstamp,
        savedByEmpId: activityConfirmation.createdEmpId,
        savedByUser: activityConfirmation.createUser,
        savedOnDeviceLabel: activityConfirmation.createdDeviceLabel,
      });
    }

    await this.keyValueService.store(
      ActivityConfirmationsLogicService.CLOUD_DRAFT_INFOS_KEY,
      infos,
    );
    this.cloudDraftInfos$.next(infos);
    this.logger.log(
      this.constructor.name,
      'updateCloudDraftInfos - drafts available for takeover',
      infos.length,
      'of',
      this.activityConfirmations$.getValue().length,
      'ACs; rejected draft candidates:',
      rejected,
    );
    await this.updateCloudDraftBadges(infos);
  }

  // Private Functions
  /**
   * Feeds the two badges on a form menu item, mirroring the two groups of the draft
   * modal: my own drafts are counted together with the local entries ("Meine
   * Formulare"), the ones parked on a service job get their own badge (the "Postkorb").
   *
   * A draft whose chain is already here locally is deliberately NOT counted: the modal
   * merges it into that local row rather than offering a second entry, and the local row
   * is already part of the MULTIINSTANCE_AMOUNT badge - counting it twice would tell the
   * user about one document more than the list shows.
   *
   * Keyed like MULTIINSTANCE_AMOUNT, except that the job section part of the objId is
   * not in the key: an AC only knows the object its form refers to, which is the same
   * scope the modal matches drafts on.
   */
  private async updateCloudDraftBadges(infos: CloudDraftInfo[]): Promise<void> {
    const myDraftAmounts: { [key: string]: number } = {};
    const serviceJobDraftAmounts: { [key: string]: number } = {};

    for (const info of infos) {
      const key = `${info.formId};${info.objRef ?? ''};${info.ownerId ?? ''}`;

      if (info.draftOwnerServiceJobId != null) {
        serviceJobDraftAmounts[key] = (serviceJobDraftAmounts[key] ?? 0) + 1;
        continue;
      }

      const isPresentLocally =
        await this.dynFormsService.hasNotFinishedDynFormDataForDraft(
          info.acukey,
          info.acId,
          info.chainRootAcId,
        );
      if (isPresentLocally) {
        continue;
      }
      myDraftAmounts[key] = (myDraftAmounts[key] ?? 0) + 1;
    }

    this.logger.log(
      this.constructor.name,
      'updateCloudDraftBadges - my drafts',
      JSON.stringify(myDraftAmounts),
      '- service job drafts',
      JSON.stringify(serviceJobDraftAmounts),
    );
    await this.badgesService.setCloudDraftAmountBadges(
      myDraftAmounts,
      serviceJobDraftAmounts,
    );
  }

  private async loadPersistedCloudDraftInfos(): Promise<void> {
    const stored: CloudDraftInfo[] | undefined = await this.keyValueService.load(
      ActivityConfirmationsLogicService.CLOUD_DRAFT_INFOS_KEY,
    );
    this.cloudDraftInfos$.next(stored ?? []);
  }

  private async loadLocalActivityConfirmations(): Promise<void> {
    try {
      this.logger.log(this.constructor.name, '- Load local activity confirmations');
      const entries = await this.activityConfirmationsDaoService.getActivityConfirmations();
      this.activityConfirmations$.next(entries);
      // Deliberately NO updateCloudDraftInfos() here. It persists its result, and at
      // initialize time employeeService.me$ is still empty - the draft owner check
      // would drop everything and overwrite the persisted list with an empty one
      // before it can be restored. Callers that actually changed ACs rebuild explicitly.
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local activity confirmations: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteActivityConfirmations(
    apiLevel: APILevel,
    syncKey: string,
  ): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    if (apiLevel < APILevel.APPVERSION_4_32) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to sync activity confirmations',
      );
      return;
    }

    this.logger.log(this.constructor.name, '- Load new activity confirmations from IMS');

    try {
      // Load everything BEFORE touching the local data. The batches are not
      // persisted one by one on purpose: a request that fails - or fails halfway
      // through the #nextid chain - must never leave the app with fewer ACs than
      // it had before. Costs no extra memory, the batch loader accumulates the
      // full list anyway.
      const entries = await this.loadRemoteActivityConfirmationBatch(syncKey, []);

      this.logger.log(
        this.constructor.name,
        'Finished loading all activity confirmations (' + entries.length + ' Total)',
      );

      // Only now replace. The backend always sends the complete set relevant for
      // this user, so wiping first is what drops the ACs that are not relevant
      // anymore - including ones stored from an upload response: if the backend
      // still considers them relevant they come right back, if not they do not
      // belong in the local store either.
      // dao.clear() instead of this.clear() so the subject is not briefly emptied.
      await this.activityConfirmationsDaoService.clear();
      await this.activityConfirmationsDaoService.saveActivityConfirmations(entries);

      await this.loadLocalActivityConfirmations();
      await this.updateCloudDraftInfos();

      // Only after everything is actually stored - the timestamp answers "how old is the
      // draft data I am looking at", so it must not move on a failed attempt.
      await this.storeLastSyncTstamp(Date.now());
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading activity confirmations: ',
        error?.message,
      );
      throw error;
    }
  }

  private async storeLastSyncTstamp(tstamp: number): Promise<void> {
    await this.keyValueService.store(
      ActivityConfirmationsLogicService.LAST_SYNC_TSTAMP_KEY,
      tstamp,
    );
    this.lastSyncTstamp$.next(tstamp);
  }

  private async loadRemoteActivityConfirmationBatch(
    syncKey: string,
    entries: ActivityConfirmation[],
    nextPart?: string,
  ): Promise<ActivityConfirmation[]> {
    let data: any;
    if (nextPart) {
      data = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey,
          },
        }),
      );
    } else {
      const additionalData = {
        synckey: syncKey,
      };
      // Same scoping as syncextfiles - the backend needs the additional jobs to
      // determine which ExtFiles (and therefore which ACs) are relevant.
      const additionalJobsIds: Servicejob[] =
        this.servicejobsService.servicejobs$.getValue() ?? [];
      additionalData['requestingAdditionalSjobs'] = additionalJobsIds
        .map((sjob) => sjob?.id?.imsId)
        .join(',');

      data = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncactivityconfirmations',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: additionalData,
        }),
      );
    }

    let nextId: string;
    const batchEntries: ActivityConfirmation[] = [];
    for (const item of data) {
      if (item['#nextid']) {
        nextId = item['#nextid'];
      } else {
        batchEntries.push(ActivityConfirmation.fromDto(item));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch activity confirmations: ',
      batchEntries.length,
      'Total',
    );

    // No persisting per batch - the caller writes the complete set in one go,
    // so a broken #nextid chain cannot leave a half-synced table behind.
    const allEntries = entries.concat(batchEntries);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more activity confirmations with nextid',
        nextId,
      );
      return await this.loadRemoteActivityConfirmationBatch(syncKey, allEntries, nextId);
    }

    return allEntries;
  }
}
